import { useState } from 'react';

import StatsSection from '../../../common/components/stats-section';
import {
    PRODUCTION_CARDS,
    RATE_ROWS,
    RECIPIENT_PRODUCTION_CARDS,
    RECIPIENT_RATE_ROWS,
} from '../../../core/constants/kpis';
import { envelopeData, envelopeModules } from '../../../core/network/envelope';
import { todayStr } from '../../../core/utils/date';
import { fmtNum, fmtPct } from '../../../core/utils/format';
import { useClientStats } from '../contexts/kpis';
import ReportSection from './report';

import '../styles/informe.css';

// TSS vite/03 — "Mis indicadores": los KPIs reproductivos agregados del
// cliente (public-kpis.md, GET public/stats?view=kpi). Dos bloques con el
// StatsSection compartido (port del StatsView del dashboard):
//   · donors_kpi (cowhousing, ancla fecha OPU) — 4 tasas;
//   · recipients_kpi (parcerías, ancla fecha T.E.) — 2 tasas.
// Cada bloque se gatea por available_modules del sobre (ausente = N/A para tu
// rol); presente con count 0 = "sin datos aún". El front NUNCA recalcula:
// números y qualifiers vienen del backend. Sin `pending`/`trends` (el canal
// público no los expone — [RIGID] public-kpis.md).
//
// D-007: la sección "Informe por finca" (view=report, partner) se monta SOLO
// si el módulo `report` viene en available_modules — el mount condicional es
// el gate de su query (view=report computa solo el informe; comparte el mismo
// rango from/to que los KPIs).

function KpiBlock({ title, meta, kpi, productionConfig, rateRows, emptyText }) {
    if (!kpi) return null;

    return (
        <section className="informe__block">
            <header className="informe__block-head">
                <h2 className="informe__block-title">{title}</h2>
                {meta && <span className="informe__block-meta">{meta}</span>}
            </header>

            {kpi.count === 0 ? (
                <p className="estats__muted">{emptyText}</p>
            ) : (
                <StatsSection
                    production={kpi.production}
                    productionConfig={productionConfig}
                    funnel={kpi.funnel}
                    rates={kpi.rates}
                    rateRows={rateRows}
                />
            )}
        </section>
    );
}

export default function InformeReproductivo() {
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');

    const { data: envelope, isLoading, isFetching, isError, error } = useClientStats('kpi', {
        from: from || undefined,
        to: to || undefined,
    });

    const data = envelopeData(envelope);
    const modules = envelopeModules(envelope);

    const donors = modules.includes('donors_kpi') ? data.donors_kpi : null;
    const recipients = modules.includes('recipients_kpi') ? data.recipients_kpi : null;

    if (isLoading) {
        return <div className="estats__state">Cargando indicadores…</div>;
    }

    return (
        <div className={`estats ${isFetching ? 'estats--busy' : ''}`}>
            <header className="estats__header">
                <h1 className="estats__title">Mis indicadores</h1>
                <p className="estats__subtitle">
                    Tus KPIs reproductivos, con los mismos números que maneja Manantiales.
                </p>
            </header>

            <div className="estats__filters">
                <label className="estats__date">
                    <span className="estats__date-label">Desde</span>
                    <input
                        type="date"
                        value={from}
                        max={to || todayStr()}
                        onChange={(e) => setFrom(e.target.value)}
                        disabled={isFetching}
                    />
                </label>
                <label className="estats__date">
                    <span className="estats__date-label">Hasta</span>
                    <input
                        type="date"
                        value={to}
                        min={from || undefined}
                        max={todayStr()}
                        onChange={(e) => setTo(e.target.value)}
                        disabled={isFetching}
                    />
                </label>
                {(from || to) && (
                    <button
                        type="button"
                        className="informe__filter-clear"
                        onClick={() => { setFrom(''); setTo(''); }}
                    >
                        Período total
                    </button>
                )}
            </div>

            {isError && (
                <div className="estats__error">
                    {error?.fields
                        ? Object.values(error.fields).join(' · ')
                        : (error?.message || 'No se pudieron cargar los indicadores.')}
                </div>
            )}

            {!isError && (
                <>
                    <KpiBlock
                        title="Cowhousing — donadoras"
                        meta={donors && donors.count > 0
                            ? `${fmtNum(donors.count)} donadora${donors.count === 1 ? '' : 's'}${donors.avg_percent != null ? ` · participación media ${fmtPct(donors.avg_percent)}` : ''}`
                            : null}
                        kpi={donors}
                        productionConfig={PRODUCTION_CARDS}
                        rateRows={RATE_ROWS}
                        emptyText="Aún no hay actividad de tus donadoras en el período."
                    />

                    <KpiBlock
                        title="Parcerías — receptoras"
                        meta={recipients && recipients.count > 0
                            ? `${fmtNum(recipients.count)} receptora${recipients.count === 1 ? '' : 's'}`
                            : null}
                        kpi={recipients}
                        productionConfig={RECIPIENT_PRODUCTION_CARDS}
                        rateRows={RECIPIENT_RATE_ROWS}
                        emptyText="Aún no hay actividad de tus receptoras en el período."
                    />

                    {modules.includes('report') && <ReportSection from={from} to={to} />}

                    {!donors && !recipients && !modules.includes('report') && (
                        <div className="estats__state">
                            No hay indicadores disponibles para tu cuenta todavía.
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
